import { formatValueDecimal } from "../../component/Methods";

/** Keys that look like identifiers — omit from UI copy / JSON previews. */
const ID_LIKE_KEY = /^(id|.*Id|.*_id|owner|managerId|strategyId|fundId|smaId|accountingId|lotId|custodian)$/i;

/**
 * Recursively drop id-like keys for safer display (e.g. blacklist rule preview).
 * @param {unknown} value
 * @param {number} depth
 * @returns {unknown}
 */
export function stripIdsDeep(value, depth = 0) {
  if (depth > 14) return "[…]";
  if (value == null) return value;
  if (Array.isArray(value)) return value.map((x) => stripIdsDeep(x, depth + 1));
  if (typeof value !== "object") return value;
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (ID_LIKE_KEY.test(k)) continue;
    out[k] = stripIdsDeep(v, depth + 1);
  }
  return out;
}

/**
 * @param {number} value
 * @returns {string} e.g. +$1,234.56 or -$0.00
 */
export function formatSignedCurrency(value) {
  const n = Number(value);
  const x = Number.isFinite(n) ? n : 0;
  const abs = formatValueDecimal(Math.abs(x));
  if (x > 0) return `+$${abs}`;
  if (x < 0) return `-$${abs}`;
  return `$${abs}`;
}

const intLocale = (absN) =>
  Math.round(Math.abs(absN)).toLocaleString("en-US", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });

/**
 * Unsigned USD for tables: whole millions with "M" when |value| ≥ $1M; otherwise whole dollars (no decimals).
 * @param {number} valueUsd
 */
export function formatAumMillions(valueUsd) {
  const n = Number(valueUsd);
  if (!Number.isFinite(n) || n === 0) return "$0";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const m = Math.round(abs / 1_000_000);
    const s = m.toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 });
    return n < 0 ? `-$${s}M` : `$${s}M`;
  }
  const s = intLocale(n);
  return n < 0 ? `-$${s}` : `$${s}`;
}

/**
 * Signed USD for fund/SMA PnL columns: whole millions ("±$NM") when |value| ≥ $1M; else signed whole dollars (no decimals).
 * @param {number} value
 */
export function formatSignedUsdCompact(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "$0";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const m = Math.round(abs / 1_000_000);
    const s = m.toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 });
    if (n > 0) return `+$${s}M`;
    return `-$${s}M`;
  }
  const s = intLocale(n);
  if (n > 0) return `+$${s}`;
  return `-$${s}`;
}
